import {
  collection,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  db
} from '../config/firebase';
import { transformDocument } from './firestoreService';
import { getCategoriesPath } from './firestorePaths';

const FETCH_CACHE_TTL_MS = 60 * 1000;
const categoriesFetchCache = new Map();

/**
 * CategoryService
 * Handles operations for categories subcollection
 */
class CategoryService {

  /**
   * Fetch all categories for a party, ordered by order descending
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @returns {Promise<Array<CategoryModel>>} Array of category models
   */
  async fetchCategories(oscarsPartyId) {
    const cachedEntry = categoriesFetchCache.get(oscarsPartyId);
    if (cachedEntry && (Date.now() - cachedEntry.timestamp) < FETCH_CACHE_TTL_MS) {
      return cachedEntry.data;
    }

    try {
      const categoriesRef = collection(db, getCategoriesPath(oscarsPartyId));
      const q = query(categoriesRef, orderBy('order', 'desc'));
      const querySnapshot = await getDocs(q);

      const categories = [];
      querySnapshot.forEach((docSnapshot) => {
        const categoryData = transformDocument(docSnapshot.id, docSnapshot.data());
        categories.push(categoryData);
      });

      categoriesFetchCache.set(oscarsPartyId, {
        data: categories,
        timestamp: Date.now(),
      });

      return categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Subscribe to categories updates in real-time
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @param {Function} callback - Function to call when categories data changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToCategories(oscarsPartyId, callback) {
    try {
      const categoriesRef = collection(db, getCategoriesPath(oscarsPartyId));
      const q = query(categoriesRef, orderBy('order', 'desc'));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const categories = [];
          snapshot.forEach((docSnapshot) => {
            const categoryData = transformDocument(docSnapshot.id, docSnapshot.data());
            categories.push(categoryData);
          });
          callback(categories);
        },
        (error) => {
          console.error('Error listening to categories:', error);
          callback([]);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up categories subscription:', error);
      callback([]);
      return () => { }; // Return no-op unsubscribe function
    }
  }
}

// Export singleton instance
export default new CategoryService();
